// terminal-result-prose-json-fallback.ts
//
// D06 finding 1 fallback-parsing helpers split out of
// terminal-result-extractor.ts to keep that file ≤200 lines. Leaf module: no
// dependency on ParsedEvent/schema — pure text scanning.

// Kept small and deterministic: 4000 chars is enough to see the tail of a
// model's JSON attempt (or its absence) without ballooning forensics files —
// tuned to the P13 brief's own figure, not a magic number invented here.
const RAW_RESULT_TAIL_MAX_CHARS = 4000;

export function rawResultTailOf(resultText: string | undefined): string | undefined {
  if (!resultText) return undefined;
  return resultText.length > RAW_RESULT_TAIL_MAX_CHARS
    ? resultText.slice(-RAW_RESULT_TAIL_MAX_CHARS)
    : resultText;
}

/** Dot-joined field path from a zod issue's `path` array (numeric segments as `[n]`, matching the brief's own `evidence[0].sha256` example). Names only — never the issue's message/received value. */
export function joinIssuePath(path: PropertyKey[]): string {
  let out = "";
  for (const segment of path) {
    if (typeof segment === "number") {
      out += `[${segment}]`;
    } else {
      out += out.length > 0 ? `.${String(segment)}` : String(segment);
    }
  }
  return out;
}

// Scans `text` left to right and returns the LAST top-level balanced `{...}`
// object substring (brace-depth walk, string/escape-aware so braces inside
// string literals don't miscount depth). "Last" because models sometimes
// think out loud with an earlier draft/example object before the real final
// answer (see D06 fixture: real smoke prose has no such case, but the
// contract must not accidentally pick an earlier, wrong object).
function findLastBalancedJsonObject(text: string): string | undefined {
  let lastMatch: string | undefined;
  let depth = 0;
  let start = -1;
  let inString = false;
  let escaped = false;

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inString) {
      if (escaped) {
        escaped = false;
      } else if (ch === "\\") {
        escaped = true;
      } else if (ch === '"') {
        inString = false;
      }
      continue;
    }
    if (ch === '"') {
      inString = true;
      continue;
    }
    if (ch === "{") {
      if (depth === 0) start = i;
      depth++;
    } else if (ch === "}") {
      if (depth > 0) {
        depth--;
        if (depth === 0 && start !== -1) {
          lastMatch = text.slice(start, i + 1);
          start = -1;
        }
      }
    }
  }

  return lastMatch;
}

// Fallback path for D06 finding 1: parse the LAST balanced JSON object out of
// the free-text `result` string (handles a ```json fenced block transparently
// since fences are just text the brace-walk ignores) and validate it against
// the same result schema structured_output would have used.
export function extractProseJsonResult(resultText: string | undefined): unknown | undefined {
  if (!resultText) return undefined;
  const candidate = findLastBalancedJsonObject(resultText);
  if (!candidate) return undefined;
  try {
    return JSON.parse(candidate);
  } catch {
    return undefined;
  }
}
